import { useCallback } from 'react'
import type { UseStreamConnectionReturn, RawConversationEventPayload } from '@/infra'
import { useStreamConnection, isRawConversationEventPayload } from '@/infra'
import { CONVERSATION_STREAM_CONFIG } from '@/index'

export interface UseConversationEventsStreamOptions {
  readonly accessToken: string | undefined
  readonly enabled: boolean
  readonly onEvent?: ((event: RawConversationEventPayload) => void) | undefined
  readonly onConnected?: ((connectionId: string) => void) | undefined
  readonly onDisconnected?: (() => void) | undefined
  readonly onError?: ((error: Error) => void) | undefined
}

export function useConversationEventsStream(
  options: UseConversationEventsStreamOptions,
): UseStreamConnectionReturn {
  const { accessToken, enabled, onEvent, onConnected, onDisconnected, onError } = options

  const handleEvent = useCallback(
    (data: string) => {
      try {
        const parsed: unknown = JSON.parse(data)
        if (isRawConversationEventPayload(parsed)) {
          onEvent?.(parsed)
        }
      } catch {
        // Silently ignore malformed events
      }
    },
    [onEvent],
  )

  const handleError = useCallback(
    (error: { message: string }) => {
      onError?.(new Error(error.message))
    },
    [onError],
  )

  return useStreamConnection({
    config: CONVERSATION_STREAM_CONFIG,
    accessToken,
    enabled,
    onEvent: handleEvent,
    onConnected,
    onDisconnected,
    onError: handleError,
  })
}
